import React, { useEffect, useMemo, useReducer, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import useTitle from 'utils/customHook/useTitle'
import i18n from 'simple-react-i18n'
import { PATH_PGSSE } from 'home/constants/RouteConstants'
import { AccordionDetails, Card, CardContent, Grid, Stack } from '@mui/material'
import useActions from 'utils/customHook/useActions'
import PgsseAction from 'pgsse/action/PgsseAction'
import Breadcrumb from '../common/BreadCrumb'
import PGSSEInfos from '../common/PGSSEInfos'
import { CardTitle } from 'components/card/NewCard'
import { NewTable } from 'components/datatable/NewTable'
import IndicatorsDialog from './IndicatorsDialog'
import { REDUCER_INDICATORS } from 'pgsse/constant/PgsseConstants'
import { compact, groupBy, isNil, keys, uniq } from 'lodash'
import { getDate, getYear } from 'utils/DateUtil'
import { getLabel } from 'utils/StoreUtils'
import Select from 'components/forms/Select'
import NewIndicatorDialog from './NewIndicatorDialog'
import moment from 'moment'

const initialState = {
    addedIndicators: [],
    updatedIndicators: [],
    deletedIndicators: [],
}

const reducer = (state, action) => {
    const { type, payload } = action
    switch (type) {
        case REDUCER_INDICATORS.ADD:
            return {
                ...state,
                addedIndicators: [ ...state.addedIndicators, ...payload ].map((d, indexAdd) => ({ ...d, indexAdd })),
            }
        case REDUCER_INDICATORS.UPDATE:
            return {
                ...state,
                addedIndicators: state.addedIndicators.map(d => payload.find(p => p.indexAdd === d.indexAdd) || d),
                updatedIndicators: [
                    ...state.updatedIndicators.filter(d => payload.some(p => isNil(p.indexAdd) && p.month === d.month && p.idIndicator === d.idIndicator && p.idOperator === d.idOperator)),
                    ...payload.filter(d => isNil(d.indexAdd)),
                ],
            }
        case REDUCER_INDICATORS.DELETE:
            return {
                ...state,
                addedIndicators: state.addedIndicators.filter(d => !payload.some(p => d.month === p.month && d.idIndicator === p.idIndicator && d.idOperator === p.idOperator)),
                updatedIndicators: state.updatedIndicators.filter(d => !payload.some(p => d.month === p.month && d.idIndicator === p.idIndicator && d.idOperator === p.idOperator)),
                deletedIndicators: compact([ ...state.deletedIndicators, ...payload ]),
            }
        case REDUCER_INDICATORS.RESET:
            return initialState
        default:
            throw new Error()
    }
}

const PgsseListApp = () => {
    const {
        diagnostic,
        indicatorValues,
        indicators,
        contributors,
    } = useSelector(store => ({
        diagnostic: store.PgsseReducer.diagnostic,
        indicatorValues: store.PgsseReducer.indicatorValues,
        indicators: store.PgsseReducer.indicators,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)

    const dispatch = useDispatch()

    const { idDiagnostic } = useParams()

    const [isOpen, setIsOpen] = useState(false)
    const [isOpenAdd, setIsOpenAdd] = useState(false)
    const [readMode, setReadMode] = useState(true)
    const [selectedOperator, setSelectedOperator] = useState()
    const [dialogOperator, setDialogOperator] = useState()

    const [stateAUD, dispatchStateAUD] = useReducer(reducer, initialState)

    useEffect(() => {
        dispatch(PgsseAction.fetchIndicators())
    }, [])

    useEffect(() => {
        dispatch(PgsseAction.fetchDiagnostic(idDiagnostic))
        dispatch(PgsseAction.fetchDiagnosticValues(idDiagnostic))
    }, [dispatch, idDiagnostic])

    useActions(() => {
        return readMode ? {
            edit: () => setReadMode(false),
        } : {
            cancel: () => {
                dispatchStateAUD({ type: REDUCER_INDICATORS.RESET })
                setReadMode(true)
            },
            save: () => {
                dispatch(PgsseAction.updateDiagnosticValues(idDiagnostic, { addedValues: stateAUD.addedIndicators, updatedValues: stateAUD.updatedIndicators, deletedValues: stateAUD.deletedIndicators })).then(() => {
                    dispatch(PgsseAction.fetchDiagnosticValues(idDiagnostic))
                    dispatchStateAUD({ type: REDUCER_INDICATORS.RESET })
                    setReadMode(true)
                })
            },
        }
    }, [readMode, idDiagnostic, stateAUD])

    useTitle(() => [{
        title: i18n.pgsse,
        href: PATH_PGSSE,
    }, {
        title: i18n.indicatorsFollow,
        href: `${PATH_PGSSE}/phase4`,
    }], [])

    const headers = ['operator', 'definition', 'year',
        { key: '01', value: i18n.januaryShort }, { key: '02', value: i18n.februaryShort }, { key: '03', value: i18n.marchShort },
        { key: '04', value: i18n.aprilShort }, { key: '05', value: i18n.mayShort }, { key: '06', value: i18n.juneShort },
        { key: '07', value: i18n.julyShort }, { key: '08', value: i18n.augustShort }, { key: '09', value: i18n.septemberShort },
        { key: '10', value: i18n.octoberShort }, { key: '11', value: i18n.novemberShort }, { key: '12', value: i18n.decemberShort }]

    const updatedData = useMemo(() => {
        return [
            ...indicatorValues.filter(di => !stateAUD.deletedIndicators.some(dei => dei.month === di.month && dei.idIndicator === di.idIndicator && dei.idOperator === di.idOperator)),
            ...stateAUD.addedIndicators,
        ].map(di => stateAUD.updatedIndicators.find(ui => ui.month === di.month && ui.idIndicator === di.idIndicator && ui.idOperator === di.idOperator) || di)
    }, [indicatorValues, stateAUD.addedIndicators, stateAUD.deletedIndicators, stateAUD.updatedIndicators])

    const data = useMemo(() => {
        const filteredData = !isNil(selectedOperator) ? updatedData.filter(d => d.idOperator === selectedOperator) : updatedData
        const grouped = groupBy(filteredData, d => `${getYear(d.month)}-${d.idOperator}-${d.idIndicator}`)
        return keys(grouped).map(key => {
            const values = grouped[key]
            const [year, idOperator, idIndicator] = key.split('-')
            return {
                idOperator: parseInt(idOperator),
                idIndicator: parseInt(idIndicator),
                operator: getLabel(contributors, idOperator),
                definition: getLabel(indicators, idIndicator, 'name', 'idIndicator'),
                ...values.reduce((acc, d) => ({ ...acc, [getDate(d.month, 'MM')]: d.value }), {}),
                year: parseInt(year),
            }
        })
    }, [contributors, indicators, selectedOperator, updatedData])

    const activeOperators = uniq(updatedData.map(d => d.idOperator))

    const lineActions = [{
        icon: 'close',
        onClick: (element) => dispatchStateAUD({
            type: REDUCER_INDICATORS.DELETE,
            payload: updatedData.filter(ud => ud.idOperator === element.idOperator && ud.idIndicator === element.idIndicator && moment(ud.month).year() === element.year),
        }),
        displayed: !readMode,
    }, {
        icon: 'edit',
        onClick: (element) => {
            setDialogOperator(element.idOperator)
            setIsOpen(true)
        },
        displayed: !readMode,
    }]

    const actions = compact([!readMode && {
        icon: 'note_add',
        tooltip: i18n.add,
        onClick: () => setIsOpenAdd(true),
    }])

    return (
        <Stack spacing={1} sx={{ padding: '1rem' }}>
            <Breadcrumb />
            <PGSSEInfos readMode diagnostic={diagnostic}/>
            {/* <Card>
                <CardTitle title={i18n.statistics} />
                <CardContent>
                    <Stack spacing={1}>
                        <Accordion
                            defaultExpanded={true}
                        >
                            <AccordionSummary>Indicateurs non actualisés</AccordionSummary>
                            <AccordionDetails>
                                <Grid container justifyContent='space-between'>
                                    <Grid>suivi des intervenants</Grid>
                                    <Grid>09/2024</Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                        <Accordion defaultExpanded={true}>
                            <AccordionSummary>{i18n.statistics}</AccordionSummary>
                            <NewTable
                                rows={[{
                                    year: 2024,
                                    indicators: 10,
                                    improving: 5,
                                    declining: 3,
                                    missing: 2,
                                }, {
                                    year: 2023,
                                    indicators: 8,
                                    improving: 3,
                                    declining: 2,
                                    missing: 3,
                                }]}
                                headers={[
                                    'year',
                                    'indicators',
                                    { key: 'improving', value: 'En amélioration' },
                                    { key: 'declining', value: 'En baisse' },
                                    { key: 'missing', value: 'Manquant' },
                                ]}
                                onClickRow={() => setIsOpen(true)}
                                WrapperComponent={AccordionDetails}
                            />
                        </Accordion>
                    </Stack>
                </CardContent>
            </Card> */}
            <Card>
                <CardTitle title={i18n.indicators} actions={actions}/>
                <CardContent>
                    <Stack spacing={1}>
                        <Grid container columnSpacing={2} alignItems='center'>
                            <Grid size={3}>
                                <Select
                                    label={i18n.operator}
                                    value={selectedOperator}
                                    options={contributors.filter(c => activeOperators.includes(c.id))}
                                    onChange={setSelectedOperator}
                                    clearFunction
                                />
                            </Grid>
                        </Grid>
                        {
                            <NewTable
                                rows={data}
                                headers={headers}
                                onClickRow={() => {}}
                                WrapperComponent={AccordionDetails}
                                lineActions={lineActions}
                            />
                        }

                    </Stack>
                </CardContent>
            </Card>
            <NewIndicatorDialog
                isOpen={isOpenAdd}
                setIsOpen={setIsOpenAdd}
                updatedData={updatedData}
                dispatchStateAUD={dispatchStateAUD}
            />
            <IndicatorsDialog
                updatedData={updatedData}
                selectedOperator={dialogOperator}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                dispatchStateAUD={dispatchStateAUD}
            />
        </Stack>
    )
}

export default PgsseListApp
